/* Homepage map and cards share the merchant-maintained store records. */
(() => {
  'use strict';
  let leafletPromise;

  function loadLeaflet() {
    if (window.L) return Promise.resolve(window.L);
    if (leafletPromise) return leafletPromise;
    leafletPromise = new Promise((resolve, reject) => {
      let script = Array.from(document.scripts).find(node => /leaflet(?:\.min)?\.js(?:\?|$)/.test(node.src) && node.dataset.micasaLeafletState !== 'error');
      const created = !script;
      if (!script) {
        script = document.createElement('script');
        script.src = window.MicasaAsset('leaflet/leaflet.js');
        script.dataset.micasaLeafletState = 'loading';
      }
      const finish = error => {
        clearTimeout(timer);
        script.removeEventListener('load', ready);
        script.removeEventListener('error', failed);
        if (error || !window.L) {
          script.dataset.micasaLeafletState = 'error';
          if (created) script.remove();
          reject(error || new Error('Map library unavailable'));
        } else {
          script.dataset.micasaLeafletState = 'ready';
          resolve(window.L);
        }
      };
      const ready = () => finish();
      const failed = () => finish(new Error('Map library unavailable'));
      const timer = setTimeout(failed, 12000);
      script.addEventListener('load', ready, { once: true });
      script.addEventListener('error', failed, { once: true });
      if (created) document.head.appendChild(script);
    }).catch(error => {
      leafletPromise = null;
      throw error;
    });
    return leafletPromise;
  }

  window.MicasaInit.register('home-store-map', scope => {
    const root = document.querySelector('[data-home-store-map-root]');
    if (!root) return;
    const section = root.closest('[data-micasa-module="mc-home-stores"]') || root.closest('.store-section') || root;
    const mapEl = root.querySelector('[data-home-store-map]');
    const listEl = root.querySelector('#store-list');
    if (!mapEl || !listEl) return;
    const conf = window.MicasaSectionConfig('home-stores');
    const defaults = {
      map_label: 'I punti vendita Micasa', list_label: 'Scegli il tuo negozio',
      map_hint: 'Tocca un punto sulla mappa per conoscere la sede.', map_reset: 'Mostra tutte le sedi',
      map_loading: 'Caricamento mappa…', map_error: 'Mappa non disponibile. Puoi consultare le sedi e aprire le indicazioni.',
      list_empty: 'Nessun punto vendita in questa selezione.', location_unknown: 'Posizione da verificare',
      results_template: '{count} punti vendita', select_label: 'Mostra sulla mappa',
      details_label: 'Reparti, orari e contatti', directions_label: 'Indicazioni', map_height: 500
    };
    const controls = { ...defaults, ...(conf.controls || {}) };
    const value = key => String(controls[key] ?? '');
    const tx = text => window.MicasaText ? window.MicasaText(text, section) : text;
    const esc = text => String(text ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
    const route = href => window.MicasaSeasonal?.url(href) || window.MicasaRoute(href, document.body.dataset.season);
    const allRecords = new Map((window.MicasaData?.stores || []).map(store => [String(store.slug), store]));
    let configuredKeys = conf.keys ?? conf.keys_stores;
    if (typeof configuredKeys === 'string') configuredKeys = configuredKeys.split(/[\n,]/).map(key => key.trim()).filter(Boolean);
    const keys = Array.isArray(configuredKeys) ? configuredKeys : Array.from(allRecords.keys());
    const stores = Array.from(new Set(keys)).map(key => allRecords.get(String(key))).filter(Boolean);
    const numbers = new Map(stores.map((store, index) => [store.slug, index + 1]));
    const buttons = Array.from(section.querySelectorAll('[data-city]'));
    const cities = buttons.map(button => button.dataset.city);
    let city = cities.includes(root.dataset.homeMapCity) ? root.dataset.homeMapCity : buttons.find(button => button.getAttribute('aria-pressed') === 'true')?.dataset.city || 'all';
    let selectedSlug = stores.some(store => store.slug === root.dataset.homeMapSelected) ? root.dataset.homeMapSelected : '';
    let visibleStores = [];
    let map, layer, loadingStarted = false, resizeTimer, scrollTimer, tileTimer;
    let libraryFailed = false, loadedTiles = 0, failedTiles = 0, ignoreCardScrollUntil = 0;
    const markers = new Map();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isMobile = () => window.matchMedia('(max-width: 760px)').matches;
    const validCoordinates = store => Number.isFinite(store.lat) && Number.isFinite(store.lng) && Math.abs(store.lat) <= 90 && Math.abs(store.lng) <= 180;
    const name = store => store.short_name || store.name || store.city || store.slug;
    const statusLabel = store => store.statusLabel || (store.status === 'soon' ? window.MicasaData?.locatorCopy?.openingLabel || tx('Prossima apertura') : '');
    const mapsUrl = store => {
      try {
        const url = new URL(String(store.googleMapsUrl || '').trim());
        return /^https?:$/.test(url.protocol) ? url.href : '';
      } catch { return ''; }
    };
    const tags = store => (Array.isArray(store.tags) ? store.tags : String(store.tags || '').split(/\r?\n/)).filter(tag => tag && (!/asiatici/i.test(tag) || store.asian) && (!/ristorazione/i.test(tag) || store.dining) && (!/supermercato/i.test(tag) || store.supermarket) && (!/^bazar$/i.test(tag) || store.bazar));

    root.classList.add('home-store-map');
    mapEl.classList.add('home-store-map__canvas');
    listEl.classList.add('home-store-map__list');
    listEl.setAttribute('role', 'list');
    listEl.setAttribute('aria-label', value('list_label'));
    mapEl.setAttribute('aria-label', value('map_label'));
    const height = Number(controls.map_height);
    root.style.setProperty('--home-store-map-height', `${Number.isFinite(height) ? Math.min(800, Math.max(280, height)) : 500}px`);
    const statusEl = root.querySelector('[data-home-store-map-status]');
    const resetButton = root.querySelector('[data-home-map-reset]');
    const hintEl = root.querySelector('.home-store-map__hint');
    const listTitle = root.querySelector('.home-store-map__list-title');
    if (listTitle) { listTitle.textContent = value('list_label'); listTitle.hidden = !value('list_label').trim(); }
    if (resetButton) { resetButton.textContent = value('map_reset'); resetButton.hidden = !value('map_reset').trim(); }
    const messageEl = document.createElement('div');
    messageEl.className = 'home-store-map__message';
    messageEl.setAttribute('role', 'status');
    messageEl.setAttribute('aria-live', 'polite');
    const messageText = document.createElement('span');
    messageEl.appendChild(messageText);
    mapEl.appendChild(messageEl);

    function showMapState(state) {
      if (!scope.active) return;
      root.dataset.mapState = state;
      mapEl.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
      messageText.textContent = state === 'error' ? value('map_error') : state === 'loading' || state === 'idle' ? value('map_loading') : '';
      messageEl.hidden = state === 'ready' || !messageText.textContent.trim();
      if (hintEl) {
        hintEl.textContent = !visibleStores.some(validCoordinates) && visibleStores.length ? value('location_unknown') : value('map_hint');
        hintEl.hidden = !hintEl.textContent.trim();
      }
    }

    function renderCards() {
      listEl.innerHTML = visibleStores.length ? visibleStores.map(store => {
        const soon = store.status === 'soon';
        const direction = mapsUrl(store);
        return `<article class="store-card home-store-map__card${soon ? ' is-opening' : ''}" data-home-store-key="${esc(store.slug)}" role="listitem">
          <div class="home-store-map__card-heading"><span class="home-store-map__number" aria-hidden="true">${String(numbers.get(store.slug)).padStart(2, '0')}</span><div><span class="home-store-map__area">${esc([store.city, store.area].filter(Boolean).join(' · '))}</span><h3>${esc(name(store))}</h3></div></div>
          ${soon && statusLabel(store) ? `<span class="home-store-map__opening">${esc(statusLabel(store))}</span>` : ''}
          ${store.address ? `<address>${esc(store.address)}</address>` : ''}
          ${store.home_description ? `<p class="home-store-map__description">${esc(store.home_description)}</p>` : ''}
          ${tags(store).length ? `<div class="store-tags home-store-map__tags">${tags(store).map(tag => `<span>${esc(tag)}</span>`).join('')}</div>` : ''}
          ${!validCoordinates(store) && value('location_unknown') ? `<p class="home-store-map__unknown">${esc(value('location_unknown'))}</p>` : ''}
          <div class="home-store-map__actions">
          ${validCoordinates(store) && value('select_label') ? `<button type="button" data-home-select-store="${esc(store.slug)}" aria-pressed="false" aria-label="${esc(`${value('select_label')} — ${name(store)}`)}">${esc(value('select_label'))}</button>` : ''}
          ${value('details_label') ? `<a href="${esc(route(`/pages/all-stores?store=${encodeURIComponent(store.slug)}`))}">${esc(value('details_label'))}<span aria-hidden="true"> ↗</span></a>` : ''}
          ${direction && value('directions_label') ? `<a href="${esc(direction)}" target="_blank" rel="noopener" aria-label="${esc(`${value('directions_label')} — ${name(store)}`)}">${esc(value('directions_label'))}<span aria-hidden="true"> ↗</span></a>` : ''}
          </div></article>`;
      }).join('') : `<p class="home-store-map__empty">${esc(value('list_empty'))}</p>`;
      if (statusEl) {
        statusEl.textContent = value('results_template').replace(/\{count\}/g, String(visibleStores.length));
        statusEl.hidden = !statusEl.textContent.trim();
      }
      updateCardSelection();
    }

    function updateCardSelection() {
      listEl.querySelectorAll('[data-home-store-key]').forEach(card => {
        const selected = card.dataset.homeStoreKey === selectedSlug;
        card.classList.toggle('is-selected', selected);
        if (selected) card.setAttribute('aria-current', 'location'); else card.removeAttribute('aria-current');
        card.querySelector('[data-home-select-store]')?.setAttribute('aria-pressed', String(selected));
      });
    }

    function scrollToCard(slug) {
      const card = Array.from(listEl.querySelectorAll('[data-home-store-key]')).find(node => node.dataset.homeStoreKey === slug);
      if (!card) return;
      const cardRect = card.getBoundingClientRect(), listRect = listEl.getBoundingClientRect();
      ignoreCardScrollUntil = Date.now() + 600;
      if (isMobile()) listEl.scrollTo({ left: listEl.scrollLeft + cardRect.left - listRect.left - 2, behavior: reduceMotion ? 'auto' : 'smooth' });
      else listEl.scrollTo({ top: listEl.scrollTop + cardRect.top - listRect.top - 4, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    function popup(store) {
      const node = document.createElement('div');
      node.className = 'home-store-map__popup';
      const label = document.createElement('strong'); label.textContent = name(store); node.appendChild(label);
      if (store.status === 'soon' && statusLabel(store)) { const badge = document.createElement('span'); badge.className = 'home-store-map__opening'; badge.textContent = statusLabel(store); node.appendChild(badge); }
      if (store.address) { const address = document.createElement('p'); address.textContent = store.address; node.appendChild(address); }
      for (const [text, href, external] of [[value('details_label'), route(`/pages/all-stores?store=${encodeURIComponent(store.slug)}`), false], [value('directions_label'), mapsUrl(store), true]]) {
        if (!text || !href) continue;
        const link = document.createElement('a'); link.textContent = `${text} ↗`; link.href = href;
        if (external) { link.target = '_blank'; link.rel = 'noopener'; }
        node.appendChild(link);
      }
      return node;
    }

    function markerIcon(store, shift = 0) {
      return window.L.divIcon({
        className: 'home-store-map__marker', iconSize: [40, 48], iconAnchor: [20, 44], popupAnchor: [shift, -43],
        html: `<span class="home-store-map__pin-wrap${store.status === 'soon' ? ' is-opening' : ''}${store.slug === selectedSlug ? ' is-selected' : ''}" style="--home-pin-shift:${shift}px;--home-pin-angle:${Math.atan2(shift,23)*180/Math.PI}deg;--home-pin-length:${Math.hypot(shift,23)}px"><i class="home-store-map__pin-stem" aria-hidden="true"></i><span class="home-store-map__pin">${numbers.get(store.slug)}</span></span>`
      });
    }

    function refreshMarkerIcons() {
      if (!map || !scope.active) return;
      const focusedSlug = document.activeElement?.dataset?.homeMarkerStore;
      const records = Array.from(markers.values());
      const points = records.map(item => map.latLngToContainerPoint([item.store.lat, item.store.lng]));
      const parents = records.map((_, index) => index), shifts = records.map(() => 0);
      const leader = index => parents[index] === index ? index : (parents[index] = leader(parents[index]));
      const merge = (a, b) => { a = leader(a); b = leader(b); if (a === b) return false; parents[b] = a; return true; };
      for (let a = 0; a < records.length; a++) for (let b = a + 1; b < records.length; b++) if (points[a].distanceTo(points[b]) < 64) merge(a, b);
      // Recheck the displayed circles after displacement, so a Pisa pair cannot cover Pontedera.
      for (let pass = 0; pass <= records.length; pass++) {
        const groups = new Map();
        records.forEach((_, index) => { const key = leader(index); if (!groups.has(key)) groups.set(key, []); groups.get(key).push(index); });
        for (const group of groups.values()) {
          if (group.length === 1) { shifts[group[0]] = 0; continue; }
          group.sort((a, b) => points[a].x - points[b].x || a - b);
          const halfSpan = (group.length - 1) * 22, width = map.getSize().x;
          let center = group.reduce((sum, index) => sum + points[index].x, 0) / group.length;
          if (halfSpan + 22 <= width / 2 && group.some(index => points[index].x >= 0 && points[index].x <= width)) center = Math.max(halfSpan + 22, Math.min(width - halfSpan - 22, center));
          group.forEach((index, rank) => { shifts[index] = center + rank * 44 - halfSpan - points[index].x; });
        }
        let joined = false;
        for (let a = 0; a < records.length; a++) for (let b = a + 1; b < records.length; b++) if (Math.hypot(points[a].x + shifts[a] - points[b].x - shifts[b], points[a].y - points[b].y) < 42) joined = merge(a, b) || joined;
        if (!joined) break;
      }
      records.forEach((item, index) => {
        const shift = shifts[index];
        item.marker.setIcon(markerIcon(item.store, shift));
        item.marker.setZIndexOffset(item.store.slug === selectedSlug ? 1000 : 0);
        const element = item.marker.getElement();
        if (element) {
          element.setAttribute('role', 'button');
          element.dataset.homeMarkerStore = item.store.slug;
          element.setAttribute('aria-label', [value('select_label'), name(item.store), item.store.status === 'soon' ? statusLabel(item.store) : ''].filter(Boolean).join(' — '));
          element.setAttribute('aria-pressed', String(item.store.slug === selectedSlug));
          if (focusedSlug === item.store.slug) element.focus({ preventScroll: true });
        }
      });
    }

    function fitResults() {
      if (!map || !scope.active) return;
      const points = visibleStores.filter(validCoordinates).map(store => [store.lat, store.lng]);
      if (points.length > 1) map.fitBounds(points, { padding: [48, 48], maxZoom: 14, animate: false });
      else if (points.length === 1) map.setView(points[0], 13, { animate: false });
      else map.setView([43.9, 10.3], 6, { animate: false });
      refreshMarkerIcons();
    }

    function selectStore(slug, options = {}) {
      const store = visibleStores.find(item => item.slug === slug);
      if (!store) return;
      selectedSlug = store.slug;
      root.dataset.homeMapSelected = selectedSlug;
      updateCardSelection();
      if (map && validCoordinates(store) && markers.has(slug)) {
        map.setView([store.lat, store.lng], 14, { animate: false });
        refreshMarkerIcons();
        markers.get(slug).marker.openPopup();
      }
      if (options.scroll !== false) scrollToCard(slug);
      if (!loadingStarted && !libraryFailed) startMap();
    }

    function renderMarkers() {
      if (!map) return;
      for (const item of markers.values()) map.removeLayer(item.marker);
      markers.clear();
      for (const store of visibleStores.filter(validCoordinates)) {
        const marker = window.L.marker([store.lat, store.lng], { icon: markerIcon(store), title: name(store), alt: name(store), keyboard: true, riseOnHover: true }).addTo(map);
        marker.bindPopup(popup(store), { maxWidth: 250, autoPan: true, autoPanPadding: [18, 18] });
        marker.on('click', () => selectStore(store.slug));
        markers.set(store.slug, { store, marker });
      }
      fitResults();
      if (selectedSlug && markers.has(selectedSlug)) selectStore(selectedSlug, { scroll: false });
    }

    function filter(nextCity) {
      city = nextCity;
      root.dataset.homeMapCity = city;
      visibleStores = stores.filter(store => city === 'all' || store.home_city === city);
      if (!visibleStores.some(store => store.slug === selectedSlug)) selectedSlug = '';
      root.dataset.homeMapSelected = selectedSlug;
      for (const button of buttons) {
        const active = button.dataset.city === city;
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', String(active));
      }
      renderCards();
      ignoreCardScrollUntil = Date.now() + 350;
      listEl.scrollTo({ left: 0, top: 0, behavior: 'auto' });
      renderMarkers();
      showMapState(root.dataset.mapState || 'idle');
    }

    function startMap() {
      if (loadingStarted || !scope.active) return;
      loadingStarted = true;
      showMapState('loading');
      loadLeaflet().then(L => {
        if (!scope.active || !mapEl.isConnected) return;
        map = L.map(mapEl, { scrollWheelZoom: false, zoomAnimation: !reduceMotion, fadeAnimation: !reduceMotion, markerZoomAnimation: !reduceMotion, zoomControl: true }).setView([43.9, 10.3], 6);
        map.on('zoomend', refreshMarkerIcons);
        layer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>' });
        layer.on('loading', () => {
          if (!scope.active) return;
          loadedTiles = 0; failedTiles = 0;
          clearTimeout(tileTimer);
          tileTimer = scope.timeout(() => { if (!loadedTiles) showMapState('error'); }, 12000);
        });
        layer.on('tileload', () => { if (!scope.active) return; loadedTiles++; if (loadedTiles === 1) showMapState('ready'); });
        layer.on('tileerror', () => { if (!scope.active) return; failedTiles++; });
        layer.on('load', () => { if (scope.active) showMapState(loadedTiles || !failedTiles ? 'ready' : 'error'); });
        layer.addTo(map);
        renderMarkers();
        scope.timeout(() => { if (map) { map.invalidateSize(); fitResults(); } }, 100);
      }).catch(() => {
        if (!scope.active) return;
        libraryFailed = true;
        showMapState('error');
      });
    }

    for (const button of buttons) scope.on(button, 'click', () => filter(button.dataset.city || 'all'));
    if (resetButton) scope.on(resetButton, 'click', () => { selectedSlug = ''; filter('all'); });
    scope.on(listEl, 'click', event => {
      if (event.target.closest('a')) return;
      const card = event.target.closest('[data-home-store-key]');
      if (card) selectStore(card.dataset.homeStoreKey, { scroll: false });
    });
    scope.on(listEl, 'scroll', () => {
      if (!isMobile() || Date.now() < ignoreCardScrollUntil) return;
      clearTimeout(scrollTimer);
      scrollTimer = scope.timeout(() => {
        const listLeft = listEl.getBoundingClientRect().left;
        const cards = Array.from(listEl.querySelectorAll('[data-home-store-key]'));
        const closest = cards.sort((a, b) => Math.abs(a.getBoundingClientRect().left - listLeft) - Math.abs(b.getBoundingClientRect().left - listLeft))[0];
        if (closest && closest.dataset.homeStoreKey !== selectedSlug) selectStore(closest.dataset.homeStoreKey, { scroll: false });
      }, 160);
    }, { passive: true });
    scope.on(mapEl, 'keydown', event => {
      const icon = event.target.closest('[data-home-marker-store]');
      if (!icon || event.key !== ' ') return;
      event.preventDefault(); event.stopPropagation();
      selectStore(icon.dataset.homeMarkerStore);
    });
    scope.on(window, 'micasa:seasonchange', () => {
      for (const item of markers.values()) item.marker.setPopupContent(popup(item.store));
      refreshMarkerIcons();
    });
    if ('ResizeObserver' in window) {
      const observer = scope.observer(ResizeObserver, () => {
        clearTimeout(resizeTimer);
        resizeTimer = scope.timeout(() => { if (map) { map.invalidateSize({ pan: false }); selectedSlug ? refreshMarkerIcons() : fitResults(); } }, 100);
      });
      observer.observe(mapEl);
    }
    filter(city);
    if ('IntersectionObserver' in window) {
      const observer = scope.observer(IntersectionObserver, entries => {
        if (entries.some(entry => entry.isIntersecting)) { observer.disconnect(); startMap(); }
      }, { rootMargin: '350px 0px' });
      observer.observe(root);
    } else startMap();
    scope.defer(() => {
      clearTimeout(resizeTimer); clearTimeout(scrollTimer); clearTimeout(tileTimer);
      if (map) { map.remove(); map = null; }
      markers.clear(); messageEl.remove();
      delete root.dataset.mapState;
    });
  });
})();
